import { useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import type { IssueFormData } from './subset-form-schema';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';
import { cn } from '@/new/lib/utils';

interface SubsetIssuesListProps {
  issues: IssueFormData[];
  onChange: (issues: IssueFormData[]) => void;
  disabled?: boolean;
  matterIssueIds?: string[];
}

export function SubsetIssuesList({ issues, onChange, disabled, matterIssueIds = [] }: SubsetIssuesListProps) {
  const matterIssueIdSet = new Set(matterIssueIds);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addText, setAddText] = useState('');

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editText, setEditText] = useState('');

  const handleAdd = () => {
    const trimmedLabel = addLabel.trim();
    if (!trimmedLabel) return;
    const trimmedText = addText.trim();
    onChange([...issues, { id: uuidv4(), label: trimmedLabel, text: trimmedText || undefined }]);
    setAddLabel('');
    setAddText('');
    setShowAddModal(false);
  };

  const handleDelete = (index: number) => {
    onChange(issues.filter((_, i) => i !== index));
  };

  const openEditModal = (index: number) => {
    const issue = issues[index];
    setEditingIndex(index);
    setEditLabel(issue.label);
    setEditText(issue.text ?? '');
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const trimmedLabel = editLabel.trim();
    const trimmedText = editText.trim();
    if (!trimmedLabel) return;
    const updated = [...issues];
    updated[editingIndex] = { ...updated[editingIndex], label: trimmedLabel, text: trimmedText || undefined };
    onChange(updated);
    setEditingIndex(null);
  };

  return (
    <div>
      <Button variant="white" size="sm" onClick={() => setShowAddModal(true)} disabled={disabled} type="button">
        <Icon className="lucide--plus" />
        Add Subset Issue
      </Button>

      {issues.length > 0 && (
        <ul className="mt-4 space-y-2">
          {issues.map((issue, i) => {
            const isMatterIssue = matterIssueIdSet.has(issue.id);
            return (
              <li key={i} className="flex items-start gap-3 text-xs">
                <span className="mt-1.5 flex-shrink-0 pt-0.5 text-gray-400 tabular-nums">{i + 1}.</span>
                <div
                  className={cn(
                    'group flex flex-1 items-center justify-between rounded-md border bg-white px-2.5 py-1.5 transition-colors',
                    disabled && 'opacity-50',
                    !disabled && !isMatterIssue && 'cursor-pointer hover:border-gray-400',
                  )}
                  onClick={() => !disabled && !isMatterIssue && openEditModal(i)}
                >
                  <span className="text-gray-700">{issue.label}</span>
                  {!disabled && (
                    <div className="ml-3 flex items-center gap-2">
                      {!isMatterIssue && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(i);
                          }}
                          className="text-gray-300 hover:text-gray-500"
                        >
                          <Icon className="lucide--pencil text-xs" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(i);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Icon className="lucide--x text-xs" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {issues.length === 0 && <p className="mt-4 text-center text-xs text-gray-400">No issues added yet.</p>}

      {/* Add Issue Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--plus" />
              Add Issue
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-subset-issue-label">Label</Label>
              <Input
                id="add-subset-issue-label"
                value={addLabel}
                onChange={(e) => setAddLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="e.g. Breach of Contract"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-subset-issue-text">
                Description <span className="text-gray-400">(optional)</span>
              </Label>
              <Textarea
                id="add-subset-issue-text"
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                rows={3}
                placeholder="Additional detail about this issue..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)} type="button">
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!addLabel.trim()} type="button">
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Issue Modal */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--pencil" />
              Edit Issue
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-subset-issue-label">Label</Label>
              <Input id="edit-subset-issue-label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-subset-issue-text">Description</Label>
              <Textarea
                id="edit-subset-issue-text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingIndex(null)} type="button">
              Cancel
            </Button>
            <Button onClick={saveEdit} type="button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
